import { BINGO_COLUMNS, columnOf, FREE, rowMissing, type BingoCard } from '../../shared/bingo.js';

// Shared pieces of the bingo pages and the host view (decision 2026-09-29).

/** Ball colours by column, like real bingo balls: B blue, I red, N white, G green, O yellow. */
export const COLUMN_COLORS = ['#2f7dff', '#ff4d5e', '#eef1f7', '#22c55e', '#facc15'] as const;
const DARKER = ['#1747a8', '#b3202f', '#9aa4b8', '#13823c', '#b58e06'] as const;

let gradientId = 0;

/** A bingo ball: coloured sphere with a white label disc, letter and number. */
export function Ball(props: { n: number; size?: number; class?: string; glow?: boolean }) {
  const col = columnOf(props.n);
  const id = `ball-${col}-${++gradientId}`;
  return (
    <svg
      class={`bingo-ball ${props.glow ? 'glow' : ''} ${props.class ?? ''}`}
      viewBox="0 0 100 100"
      width={props.size ?? 64}
      height={props.size ?? 64}
      role="img"
      aria-label={`${BINGO_COLUMNS[col]} ${props.n}`}
    >
      <defs>
        <radialGradient id={id} cx="35%" cy="30%" r="75%">
          <stop offset="0" stop-color="#ffffff" stop-opacity="0.9" />
          <stop offset="0.25" stop-color={COLUMN_COLORS[col]} />
          <stop offset="1" stop-color={DARKER[col]} />
        </radialGradient>
      </defs>
      <circle cx="50" cy="50" r="48" fill={`url(#${id})`} />
      <circle cx="50" cy="52" r="29" fill="#ffffff" stroke="rgba(0,0,0,0.12)" stroke-width="1.5" />
      <text x="50" y="44" text-anchor="middle" font-size="15" font-weight="800" fill="#1a2233" font-family="system-ui, 'Segoe UI', sans-serif">
        {BINGO_COLUMNS[col]}
      </text>
      <text x="50" y="68" text-anchor="middle" font-size={props.n >= 10 ? 27 : 30} font-weight="900" fill="#0d1220" font-family="system-ui, 'Segoe UI', sans-serif">
        {props.n}
      </text>
      <ellipse cx="34" cy="24" rx="14" ry="7" fill="#fff" opacity="0.35" transform="rotate(-25 34 24)" />
    </svg>
  );
}

/** All 75 numbers in five rows (B I N G O); drawn numbers light up in their column colour. */
export function NumberBoard(props: { draws: number[]; class?: string }) {
  const drawn = new Set(props.draws);
  const last = props.draws[props.draws.length - 1];
  return (
    <div class={`bingo-board ${props.class ?? ''}`}>
      {BINGO_COLUMNS.map((letter, c) => (
        <div class="bingo-board-row" key={letter}>
          <span class="bingo-board-letter" style={{ background: COLUMN_COLORS[c], color: c === 2 || c === 4 ? '#1a2233' : '#fff' }}>
            {letter}
          </span>
          {Array.from({ length: 15 }, (_, i) => {
            const n = c * 15 + i + 1;
            const on = drawn.has(n);
            return (
              <span
                key={n}
                class={`bingo-board-cell ${on ? 'on' : ''} ${n === last ? 'last' : ''}`}
                style={on ? { background: COLUMN_COLORS[c], color: c === 2 || c === 4 ? '#1a2233' : '#fff' } : undefined}
              >
                {n}
              </span>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/**
 * A coupon grid; `marked` squares show a dab. Tapping calls `onToggle` (the player marks).
 * The free square can show a sponsor logo (user request 2026-09-29).
 */
export function CouponGrid(props: {
  cells: BingoCard;
  marked?: ReadonlySet<number>;
  /** Numbers still needed for a bingo are highlighted (the admin view, 2026-10-01). */
  needed?: ReadonlySet<number>;
  onToggle?: (n: number) => void;
  sponsorUrl?: string | null;
  class?: string;
  /** Full rows (all marked) are highlighted (the own coupons, user request 2026-10-02). */
  fullRows?: boolean;
}) {
  const full = new Set<number>();
  if (props.fullRows && props.marked) rowMissing(props.cells, props.marked).forEach((m, r) => m === 0 && full.add(r));
  const row = (i: number) => (full.has(Math.floor(i / 5)) ? ' row-full' : '');
  return (
    <div class={`coupon-grid ${props.class ?? ''}`} role="grid">
      {BINGO_COLUMNS.map((letter, c) => (
        <span class="coupon-head" key={letter} style={{ background: COLUMN_COLORS[c], color: c === 2 || c === 4 ? '#1a2233' : '#fff' }}>
          {letter}
        </span>
      ))}
      {props.cells.map((n, i) =>
        n === FREE ? (
          <span class={`coupon-cell free ${props.marked ? 'marked' : ''}${row(i)}`} key={`f${i}`} role="gridcell">
            {props.sponsorUrl ? <img src={props.sponsorUrl} alt="Sponsori" /> : <>★<small>VAPAA</small></>}
          </span>
        ) : (
          <button
            type="button"
            key={n}
            role="gridcell"
            class={`coupon-cell ${props.marked?.has(n) ? 'marked' : ''} ${props.needed?.has(n) ? 'needed' : ''}${row(i)}`}
            onClick={() => props.onToggle?.(n)}
            disabled={!props.onToggle}
            aria-pressed={props.marked?.has(n) ?? false}
          >
            {n}
          </button>
        ),
      )}
    </div>
  );
}

/** Areena emblem for the bingo pages. */
export function BingoLogo(props: { size?: number }) {
  return (
    <span class="bingo-logo">
      <svg viewBox="0 0 48 48" width={props.size ?? 40} height={props.size ?? 40} aria-hidden="true">
        <defs>
          <linearGradient id="bingo-ring" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stop-color="#5cc8ff" />
            <stop offset="1" stop-color="#a78bfa" />
          </linearGradient>
        </defs>
        <path d="M30.5 5.2A20 20 0 1 1 17.5 5.2" fill="none" stroke="url(#bingo-ring)" stroke-width="4" stroke-linecap="round" />
        <path d="M24 9 L13 37 M24 9 L35 37" stroke="#e8ecf6" stroke-width="4" stroke-linecap="round" fill="none" />
        <path d="M24 22.5l1.6 3.4 3.7.4-2.8 2.5.8 3.7-3.3-1.9-3.3 1.9.8-3.7-2.8-2.5 3.7-.4z" fill="#facc15" />
      </svg>
      <span class="bingo-logo-word">
        AREENA <b>BINGO</b>
      </span>
    </span>
  );
}

/** Address of the sponsor logo, or null. */
export const sponsorUrl = (version: number | null | undefined) => (version ? `/api/bingo/sponsor?v=${version}` : null);

/** "pe 3.10. klo 19.00" */
export function nextDrawText(at: number): string {
  const d = new Date(at);
  const day = d.toLocaleDateString('fi-FI', { weekday: 'short' });
  const date = `${d.getDate()}.${d.getMonth() + 1}.`;
  const time = d.toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit' });
  return `${day} ${date} klo ${time}`;
}

/** "12:34" or "1:02:03" (the countdown to the first number, 2026-10-01). */
export function countdownText(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = String(s % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
}

/** The countdown to the first number; gone when the time is up (shown only within a day). */
export function Countdown(props: { at: number; now: number; class?: string; label?: string }) {
  const left = props.at - props.now;
  if (left <= 0 || left > 24 * 60 * 60 * 1000) return null;
  return (
    <div class={`countdown ${props.class ?? ''}`} role="timer" aria-live="off">
      <span class="countdown-label">{props.label ?? 'Ensimmäiseen numeroon'}</span>
      <span class="countdown-time">{countdownText(left)}</span>
    </div>
  );
}
